import { useState, useEffect, useCallback } from 'react';
import { 
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { getDoc, doc } from 'firebase/firestore';
import { auth, firestore } from '../services/firebase/firebase';
import { startPresence, stopPresence } from '../services/presenceService';
import { toast } from 'react-toastify';

export function useAuth() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);

  // Fonction de déconnexion
  const logout = useCallback(async () => {
    setError(null);
    // Présence : marquer le staff hors ligne avant la déconnexion
    stopPresence();
    try {
      await signOut(auth);
      console.log("Logout successful");
    } catch (error) {
      console.error("Logout error:", error);
      setError('Erreur lors de la déconnexion.');
    }
  }, []);

  // Vérifier si l'utilisateur est admin
  const checkAdminStatus = useCallback(async (user) => {
    if (!user) {
      setIsAdmin(false);
      return false;
    }
    
    try {
      const userDoc = await getDoc(doc(firestore, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const hasAdminRights = userData.isAdmin === true;
        setIsAdmin(hasAdminRights);
        console.log("Admin status:", hasAdminRights ? "Administrator" : "Not an admin");
        return hasAdminRights;
      } else {
        setIsAdmin(false);
        console.log("User document not found in Firestore");
        return false;
      }
    } catch (error) {
      console.error("Error checking admin status:", error);
      setIsAdmin(false);
      return false;
    }
  }, []);

  // Observer les changements d'état d'authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Auth state changed:", user ? "User logged in" : "No user");
      
      // Mettre à jour l'utilisateur d'abord
      setCurrentUser(user);

      // Presence du staff : demarrage a la connexion, arret a la deconnexion.
      // (Non affichee en v1 - prevue pour le futur « conseiller en ligne » cote client.)
      if (user) {
        startPresence(user.uid, { role: 'staff' });
      } else {
        stopPresence();
      }
      
      // Vérifier les droits d'administrateur si un utilisateur est connecté
      if (user) {
        // Vérifier les droits d'administrateur après la mise à jour de l'utilisateur
        try {
          const userDoc = await getDoc(doc(firestore, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const hasAdminRights = userData.isAdmin === true;
            setIsAdmin(hasAdminRights);
            console.log("Admin status:", hasAdminRights ? "Administrator" : "Not an admin");
          } else {
            setIsAdmin(false);
            console.log("User document not found in Firestore");
          }
        } catch (error) {
          console.error("Error checking admin status:", error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      
      // Marquer l'authentification comme initialisée après toutes les vérifications
      setLoading(false);
      setAuthInitialized(true);
    }, (error) => {
      console.error("Auth state change error:", error);
      setError(error.message);
      setLoading(false);
      setAuthInitialized(true);
    });

    return () => unsubscribe();
  }, []);

  // Fonction de connexion
  const login = async (email, password) => {
    setError(null);
    try {
      // Utiliser la persistance locale pour garder l'utilisateur connecté
      await setPersistence(auth, browserLocalPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log("Login successful:", userCredential.user.email);
      
      // Vérifier immédiatement si l'utilisateur est administrateur
      const isUserAdmin = await checkAdminStatus(userCredential.user);
      
      if (!isUserAdmin) {
        console.log("User is not admin, signing out");
        await signOut(auth);
        setError("Accès refusé: Vous n'avez pas les droits d'administration.");
        return null;
      }
      
      return userCredential.user;
    } catch (error) {
      console.error("Login error:", error.code, error.message);
      setError(
        error.code === 'auth/invalid-credential' 
          ? 'Identifiants incorrects. Veuillez vérifier votre email et mot de passe.'
          : `Erreur lors de la connexion: ${error.message}`
      );
      return null;
    }
  };

  return { 
    currentUser, 
    loading, 
    error, 
    isAdmin, 
    authInitialized,
    login, 
    logout,
    checkAdminStatus 
  };
}